// components/EmployeeDashboard.js - VERSION RESPONSIVE AMÉLIORÉE
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, Alert, Spinner, Nav, Tab } from 'react-bootstrap';
import { 
  ShoppingCart, 
  TrendingUp, 
  Calendar, 
  Target, 
  Clock, 
  AlertTriangle,
  RefreshCw,
  User,
  DollarSign,
  BarChart3,
  List,
  Package
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom'; // Ajout pour la navigation
import employeeService from '../../services/employeeService';

// Importer les nouveaux composants
import MySalesComponent from '../employee/MySalesComponent';
import MyStatsComponent from '../employee/MyStatsComponent';

const EmployeeDashboard = () => {
  const { user } = useAuth();
  const { getButtonClass, getCardStyle, getBadgeClass, primaryColor, secondaryColor } = useTheme();
  const navigate = useNavigate(); // Hook de navigation
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [activeTab, setActiveTab] = useState('overview');
  
  // État pour gérer le responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 768 && window.innerWidth < 992);

  // Gestion du responsive
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Charger les vraies données depuis la base de données
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setDashboardLoading(true);
      setError(null);
      
      const response = await employeeService.getDashboard();
      
      if (response.success) {
        setDashboardData(response.data);
        setLastUpdate(new Date());
      } else {
        throw new Error(response.message || 'Erreur lors du chargement du dashboard');
      }
    } catch (error) {
      console.error('Erreur chargement dashboard employé:', error);
      setError(error.message);
      
      // Fallback avec données vides
      setDashboardData({
        myDailySales: { salesCount: 0, revenue: 0, lastSale: 'Aucune' },
        shopTotalToday: { salesCount: 0, revenue: 0 },
        myWeeklyStats: { salesCount: 0, revenue: 0, avgSaleValue: 0 },
        myMonthlyStats: { ranking: 1, target: 0, achieved: 0, progressPercent: 0 },
        topProducts: [],
        stockAlerts: [],
        recentSales: [],
        creditSales: []
      });
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadDashboardData();
  };

  // Navigation vers le POS
  const handleNewSale = () => {
    navigate('/pos'); // Redirection vers le point de vente
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 F CFA';
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount) + ' F CFA';
  };

  // Fonction pour déterminer la couleur du badge de stock
  const getStockBadgeVariant = (stock, minLevel = 5) => {
    if (stock === 0) return 'danger';
    if (stock <= minLevel) return 'warning';
    return 'success';
  };

  // Composant StatCard responsive
  const StatCard = ({ icon: Icon, title, value, subtitle, variant = 'primary', trend = null }) => (
    <Card style={getCardStyle(true)} className="h-100 stat-card-responsive">
      <Card.Body className={`d-flex align-items-center ${isMobile ? 'p-3' : 'p-4'}`}>
        <div 
          className={`d-flex align-items-center justify-content-center me-3 position-relative stat-icon-container`}
          style={{
            width: isMobile ? '45px' : '60px',
            height: isMobile ? '45px' : '60px',
            borderRadius: '50%',
            backgroundColor: `rgba(${variant === 'success' ? '16, 185, 129' : 
                                   variant === 'info' ? '6, 182, 212' : 
                                   variant === 'warning' ? '245, 158, 11' : 
                                   'var(--bs-primary-rgb)'}, 0.1)`
          }}
        >
          <Icon size={isMobile ? 20 : 24} style={{ color: variant === 'success' ? '#10b981' : 
                                          variant === 'info' ? '#06b6d4' : 
                                          variant === 'warning' ? '#f59e0b' : 
                                          primaryColor }} />
          {trend && (
            <Badge 
              bg={trend > 0 ? 'success' : 'danger'} 
              className="position-absolute"
              style={{ top: '-8px', right: '-8px', fontSize: '0.65rem' }}
            >
              {trend > 0 ? '+' : ''}{trend}%
            </Badge>
          )}
        </div>
        <div className="flex-grow-1 stat-info">
          <h3 className={`mb-1 fw-bold stat-value`} style={{ 
            fontSize: isMobile ? '1.4rem' : '2rem', 
            color: '#1f2937',
            lineHeight: '1.2'
          }}>{value}</h3>
          <p className={`mb-1 fw-semibold text-muted stat-title`} style={{ 
            fontSize: isMobile ? '0.8rem' : '0.95rem'
          }}>{title}</p>
          {subtitle && (
            <small className="text-muted stat-subtitle" style={{ 
              fontSize: isMobile ? '0.7rem' : '0.85rem',
              display: isMobile ? 'block' : 'inline',
              lineHeight: isMobile ? '1.3' : '1.4'
            }}>{subtitle}</small>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  if (dashboardLoading) {
    return (
      <div style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
        minHeight: '100vh' 
      }}>
        <Container fluid>
          <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
            <div className="text-center">
              <Spinner animation="border" style={{ color: primaryColor }} size="lg" />
              <p className="mt-3">Chargement de votre tableau de bord...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)', 
        minHeight: '100vh', 
        padding: isMobile ? '1rem 0' : '1.5rem 0'
      }}
    >
      <Container fluid className={isMobile ? 'px-2' : ''}>
        {/* En-tête employé responsive */}
        <div 
          className="mb-4 position-relative overflow-hidden header-responsive"
          style={{
            background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            borderRadius: 'var(--border-radius-lg)',
            color: 'white'
          }}
        >
          <div className={`d-flex ${isMobile ? 'flex-column' : 'justify-content-between align-items-center'} position-relative header-content`} 
               style={{ 
                 zIndex: 2,
                 padding: isMobile ? '1.5rem 1rem' : '2rem 1.5rem'
               }}>
            <div className={isMobile ? 'text-center mb-3' : ''}>
              <h1 className="mb-2 fw-bold header-title" style={{ 
                fontSize: isMobile ? '1.5rem' : '2rem', 
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
              }}>
                Bonjour {user?.full_name || 'Employé'} !
              </h1>
              <p className="mb-1 header-date" style={{ 
                fontSize: isMobile ? '0.9rem' : '1.1rem', 
                opacity: 0.9 
              }}>
                {new Date().toLocaleDateString('fr-FR', { 
                  weekday: isMobile ? 'short' : 'long', 
                  year: 'numeric', 
                  month: isMobile ? 'short' : 'long', 
                  day: 'numeric' 
                })}
              </p>
              {dashboardData?.myDailySales && (
                <p className="mb-1 header-stats" style={{ 
                  fontSize: isMobile ? '0.8rem' : '0.95rem', 
                  opacity: 0.8 
                }}>
                  {dashboardData.myDailySales.salesCount} vente{dashboardData.myDailySales.salesCount > 1 ? 's' : ''} aujourd'hui
                  {dashboardData.myDailySales.lastSaleAmount > 0 && !isMobile && (
                    <span> • Dernière: {formatCurrency(dashboardData.myDailySales.lastSaleAmount)}</span>
                  )}
                </p>
              )}
              <small style={{ opacity: 0.7, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                Dernière mise à jour : {lastUpdate.toLocaleTimeString('fr-FR')}
              </small>
            </div>
            <div className={`d-flex ${isMobile ? 'flex-column w-100' : 'gap-3'} align-items-center header-actions`}>
              <Button 
                variant="success" 
                size={isMobile ? "lg" : "lg"}
                className={`d-flex align-items-center gap-2 ${isMobile ? 'w-100 mb-2 justify-content-center' : ''}`}
                onClick={handleNewSale} // Ajout de la navigation
                style={{ 
                  fontWeight: '600',
                  border: 'none',
                  borderRadius: 'var(--border-radius-sm)',
                  boxShadow: 'var(--shadow-md)',
                  padding: isMobile ? '0.8rem 1.5rem' : '0.75rem 1.5rem'
                }}
              >
                <ShoppingCart size={20} />
                Nouvelle Vente
              </Button>
              <Button 
                variant="outline-light"
                onClick={handleRefresh}
                disabled={dashboardLoading}
                className={`d-flex align-items-center gap-2 ${isMobile ? 'w-100 justify-content-center' : ''}`}
                size={isMobile ? "lg" : "md"}
                style={{ 
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 'var(--border-radius-sm)',
                  padding: isMobile ? '0.8rem 1.5rem' : '0.5rem 1rem'
                }}
              >
                <RefreshCw size={16} className={dashboardLoading ? 'spin' : ''} />
                {dashboardLoading ? 'Actualisation...' : 'Actualiser'}
              </Button>
            </div>
          </div>
        </div>

        {/* Alerte d'erreur si présente */}
        {error && (
          <Alert variant="warning" className="mb-4">
            <AlertTriangle size={16} className="me-2" />
            {error} - Les données affichées peuvent être incomplètes.
          </Alert>
        )}

        {/* ONGLETS RESPONSIVE */}
        <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
          {/* Navigation des onglets responsive */}
          <Card className="mb-4 tabs-container">
            <Card.Body className={`${isMobile ? 'py-2 px-2' : 'py-3'}`}>
              <Nav variant="pills" className={`${isMobile ? 'nav-mobile' : 'justify-content-center'}`}>
                <Nav.Item className={isMobile ? 'flex-fill' : ''}>
                  <Nav.Link 
                    eventKey="overview" 
                    className={`d-flex align-items-center gap-2 ${isMobile ? 'flex-column text-center py-2' : ''}`}
                  >
                    <TrendingUp size={16} />
                    {isMobile ? (
                      <span style={{ fontSize: '0.75rem' }}>Vue d'ensemble</span>
                    ) : (
                      "Vue d'ensemble"
                    )}
                  </Nav.Link>
                </Nav.Item>
                {/* <Nav.Item className={isMobile ? 'flex-fill' : ''}>
                  <Nav.Link 
                    eventKey="my-sales" 
                    className={`d-flex align-items-center gap-2 ${isMobile ? 'flex-column text-center py-2' : ''}`}
                  >
                    <List size={16} />
                    {isMobile ? (
                      <span style={{ fontSize: '0.75rem' }}>Mes Ventes</span>
                    ) : (
                      'Mes Ventes'
                    )}
                  </Nav.Link>
                </Nav.Item> */}
                <Nav.Item className={isMobile ? 'flex-fill' : ''}>
                  <Nav.Link 
                    eventKey="my-stats" 
                    className={`d-flex align-items-center gap-2 ${isMobile ? 'flex-column text-center py-2' : ''}`}
                  >
                    <BarChart3 size={16} />
                    {isMobile ? (
                      <span style={{ fontSize: '0.75rem' }}>Statistiques</span>
                    ) : (
                      'Mes Statistiques'
                    )}
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Body>
          </Card>

          {/* Contenu des onglets */}
          <Tab.Content>
            {/* Onglet Vue d'ensemble */}
            <Tab.Pane eventKey="overview">
              {/* Statistiques rapides responsive */}
              <Row className="mb-4 stats-grid">
                <Col xs={6} lg={3} className="mb-3">
                  <StatCard
                    icon={ShoppingCart}
                    title="Mes Ventes Aujourd'hui"
                    value={dashboardData?.myDailySales?.salesCount || 0}
                    subtitle={`${formatCurrency(dashboardData?.myDailySales?.revenue || 0)}${isMobile ? '' : ` • Dernière: ${dashboardData?.myDailySales?.lastSale || 'Aucune'}`}`}
                    variant="success"
                  />
                </Col>
                <Col xs={6} lg={3} className="mb-3">
                  <StatCard
                    icon={TrendingUp}
                    title="Total Boutique Aujourd'hui"
                    value={dashboardData?.shopTotalToday?.salesCount || 0}
                    subtitle={formatCurrency(dashboardData?.shopTotalToday?.revenue || 0)}
                    variant="primary"
                  />
                </Col>
                <Col xs={6} lg={3} className="mb-3">
                  <StatCard
                    icon={Calendar}
                    title="Ma Semaine"
                    value={dashboardData?.myWeeklyStats?.salesCount || 0}
                    subtitle={`${formatCurrency(dashboardData?.myWeeklyStats?.revenue || 0)}${isMobile ? '' : ` • Moy: ${formatCurrency(dashboardData?.myWeeklyStats?.avgSaleValue || 0)}`}`}
                    variant="info"
                  />
                </Col>
                <Col xs={6} lg={3} className="mb-3">
                  <StatCard
                    icon={Target}
                    title="Mon Classement"
                    value={`#${dashboardData?.myMonthlyStats?.ranking || 1}`}
                    subtitle={`${Math.round(dashboardData?.myMonthlyStats?.progressPercent || 0)}% ${isMobile ? 'objectif' : `de l'objectif • ${dashboardData?.myMonthlyStats?.teamSize || 1} employés`}`}
                    variant="warning"
                  />
                </Col>
              </Row>

              {/* Cartes supplémentaires responsive */}
              <Row className="content-cards">
               <Col lg={4} className="mb-4">
  <Card style={getCardStyle()} className="h-100">
    <Card.Header className="bg-transparent border-bottom">
      <h6 className={`mb-0 fw-bold ${isMobile ? 'text-center' : ''}`} style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
        🏆 Top Produits du Jour
      </h6>
    </Card.Header>
    <Card.Body className={isMobile ? 'p-2' : ''}>
      {dashboardData?.topProducts?.length > 0 ? (
        <div>
          {dashboardData.topProducts.slice(0, isMobile ? 3 : 5).map((product, index) => (
            <div key={product.id || index} className={`d-flex align-items-center justify-content-between ${isMobile ? 'p-2 mb-2' : 'p-3 mb-3'} product-item`}>
              {/* Rang et info produit */}
              <div className="d-flex align-items-center flex-grow-1">
                <div 
                  className="product-rank"
                  style={{
                    width: isMobile ? '24px' : '28px',
                    height: isMobile ? '24px' : '28px',
                    borderRadius: '50%',
                    background: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : primaryColor,
                    color: index < 3 ? '#000' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: isMobile ? '0.7rem' : '0.8rem',
                    fontWeight: '700',
                    marginRight: isMobile ? '0.5rem' : '0.75rem',
                    flexShrink: 0
                  }}
                >
                  {index + 1}
                </div>
                
                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                  <div 
                    className="product-name fw-semibold" 
                    style={{ 
                      fontSize: isMobile ? '0.8rem' : '0.9rem',
                      color: '#2c3e50',
                      marginBottom: '0.25rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {product.product_name || product.productName || product.name || 'Produit inconnu'}
                  </div>
                  
                  <div 
                    className="product-stats" 
                    style={{ 
                      fontSize: isMobile ? '0.7rem' : '0.75rem',
                      color: '#6c757d',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <span>
                      <Package size={12} className="me-1" />
                      {product.total_sold || product.totalSold || product.sales || 0} vendus
                    </span>
                    
                    {!isMobile && (
                      <span>
                        • {formatCurrency(product.total_revenue || product.totalRevenue || product.revenue || 0)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Revenus */}
              <div className="text-end" style={{ flexShrink: 0 }}>
                <div 
                  className="product-revenue fw-bold text-success" 
                  style={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}
                >
                  {formatCurrency(product.total_revenue || product.totalRevenue || product.revenue || 0)}
                </div>
                
                {!isMobile && (product.total_sold || product.totalSold || product.sales) && (
                  <Badge 
                    bg="primary" 
                    pill 
                    style={{ fontSize: '0.7rem' }}
                  >
                    {product.total_sold || product.totalSold || product.sales} ventes
                  </Badge>
                )}
              </div>
            </div>
          ))}
          
          {/* Voir plus */}
          {dashboardData.topProducts.length > (isMobile ? 3 : 5) && (
            <div className="text-center pt-2 border-top">
              <Button 
                variant="outline-primary" 
                size="sm"
                style={{ fontSize: isMobile ? '0.8rem' : '0.85rem' }}
              >
                <TrendingUp size={14} className="me-1" />
                Voir plus ({dashboardData.topProducts.length - (isMobile ? 3 : 5)} autres)
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center text-muted py-4">
          <ShoppingCart size={isMobile ? 32 : 48} className="opacity-50 mb-3" />
          <p style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>Aucune vente aujourd'hui</p>
          <small className="text-muted">
            Les produits les plus vendus apparaîtront ici
          </small>
        </div>
      )}
    </Card.Body>
  </Card>
</Col>

                <Col lg={4} md={6} className="mb-4">
                  <Card style={getCardStyle()} className="h-100 content-card">
                    <Card.Header className="bg-transparent border-bottom">
                      <h6 className={`mb-0 fw-bold ${isMobile ? 'text-center' : ''}`} style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                        ⚠️ Alertes Stock
                      </h6>
                    </Card.Header>
                    <Card.Body className={isMobile ? 'p-2' : ''}>
                      {dashboardData?.stockAlerts?.length > 0 ? (
                        <div>
                          {dashboardData.stockAlerts.slice(0, isMobile ? 3 : 5).map((item, index) => (
                            <div key={item.id || index} className={`d-flex align-items-center justify-content-between ${isMobile ? 'p-2 mb-2' : 'p-3 mb-2'} alert-item`}>
                              <div className="fw-semibold" style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                {item.productName || item.name}
                              </div>
                              <Badge bg={getStockBadgeVariant(item.currentStock || item.quantity)} style={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                                {item.currentStock || item.quantity} restant
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <AlertTriangle size={isMobile ? 32 : 48} className="opacity-50 mb-3" />
                          <p style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>Aucune alerte de stock</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col lg={4} md={12} className="mb-4">
                  <Card style={getCardStyle()} className="h-100 content-card">
                    <Card.Header className="bg-transparent border-bottom">
                      <h6 className={`mb-0 fw-bold ${isMobile ? 'text-center' : ''}`} style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}>
                        🕒 Ventes Récentes
                      </h6>
                    </Card.Header>
                    <Card.Body className={isMobile ? 'p-2' : ''}>
                      {dashboardData?.recentSales?.length > 0 ? (
                        <div>
                          {dashboardData.recentSales.slice(0, isMobile ? 3 : 5).map((sale, index) => (
                            <div key={sale.id || index} className={`${isMobile ? 'p-2 mb-2' : 'p-3 mb-2'} sale-item`}>
                              <div className="fw-semibold" style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>
                                {sale.customer}
                              </div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <Clock size={isMobile ? 12 : 16} />
                                <small className="text-muted" style={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}>
                                  {sale.time}
                                </small>
                              </div>
                              <div className="fw-bold text-success" style={{ fontSize: isMobile ? '0.85rem' : '1rem' }}>
                                {formatCurrency(sale.amount)}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <Clock size={isMobile ? 32 : 48} className="opacity-50 mb-3" />
                          <p style={{ fontSize: isMobile ? '0.8rem' : '1rem' }}>Aucune vente récente</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab.Pane>

            {/* Onglet Mes Ventes */}
            <Tab.Pane eventKey="my-sales">
              <MySalesComponent />
            </Tab.Pane>

            {/* Onglet Mes Statistiques */}
            <Tab.Pane eventKey="my-stats">
              <MyStatsComponent />
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>

      {/* Styles CSS responsives */}
      <style jsx>{`
        @media (max-width: 768px) {
          .header-responsive {
            margin-left: -0.5rem;
            margin-right: -0.5rem;
            border-radius: 0 0 1rem 1rem !important;
          }
          
          .header-content {
            padding: 1.5rem 1rem !important;
          }
          
          .header-title {
            font-size: 1.5rem !important;
          }
          
          .header-date {
            font-size: 0.9rem !important;
          }
          
          .header-stats {
            font-size: 0.8rem !important;
          }
          
          .header-actions {
            width: 100%;
            gap: 0.5rem;
          }
          
          .tabs-container .nav-mobile {
            padding: 0;
          }
          
          .tabs-container .nav-mobile .nav-item {
            flex: 1;
          }
          
          .tabs-container .nav-mobile .nav-link {
            border-radius: 0.5rem;
            margin: 0 0.125rem;
            padding: 0.5rem 0.25rem;
          }
          
          .stats-grid .col-6 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .stat-card-responsive {
            border-radius: 0.75rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          .stat-icon-container {
            margin-right: 0.75rem !important;
          }
          
          .stat-value {
            font-size: 1.4rem !important;
          }
          
          .stat-title {
            font-size: 0.8rem !important;
            line-height: 1.2;
          }
          
          .stat-subtitle {
            font-size: 0.7rem !important;
            line-height: 1.3;
          }
          
          .content-cards .col-lg-4 {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .content-card {
            border-radius: 0.75rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          
          .content-card .card-header {
            padding: 0.75rem;
          }
          
          .content-card .card-body {
            padding: 0.5rem !important;
          }
          
          .product-item,
          .alert-item,
          .sale-item {
            background: rgba(0,0,0,0.02);
            border-radius: 0.5rem;
            border: 1px solid rgba(0,0,0,0.05);
          }
        }
        
        @media (max-width: 576px) {
          .container-fluid {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
          }
          
          .stats-grid {
            margin-left: -0.25rem;
            margin-right: -0.25rem;
          }
          
          .stats-grid .col-6 {
            padding-left: 0.25rem;
            padding-right: 0.25rem;
          }
          
          .stat-value {
            font-size: 1.2rem !important;
          }
          
          .stat-title {
            font-size: 0.75rem !important;
          }
          
          .stat-subtitle {
            font-size: 0.65rem !important;
          }
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
  .product-item {
    background: rgba(0,0,0,0.02);
    border-radius: 8px;
    border: 1px solid rgba(0,0,0,0.05);
    transition: all 0.2s ease;
  }
  
  .product-item:hover {
    background: rgba(0,0,0,0.05);
    transform: translateY(-1px);
  }
  
  .product-item:last-child {
    margin-bottom: 0 !important;
  }
  
  .product-rank {
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .product-name {
    line-height: 1.2;
  }
  
  .product-stats {
    line-height: 1.3;
  }
  
  .product-revenue {
    line-height: 1.2;
  }
  
  @media (max-width: 768px) {
    .product-item {
      padding: 0.5rem !important;
      margin-bottom: 0.5rem !important;
    }
    
    .product-rank {
      width: 20px !important;
      height: 20px !important;
      font-size: 0.6rem !important;
      margin-right: 0.5rem !important;
    }
    
    .product-name {
      font-size: 0.75rem !important;
    }
    
    .product-stats {
      font-size: 0.65rem !important;
    }
    
    .product-revenue {
      font-size: 0.75rem !important;
    }
  }
  
  @media (max-width: 576px) {
    .product-stats span:not(:first-child) {
      display: none;
    }
  }

      `}</style>
    </div>
  );
};

export default EmployeeDashboard;